import { Component, HostListener, Input, OnDestroy } from '@angular/core';
import { MergeTagPickerOverlayService, MergeTagPickerSession } from '../merge-tag-picker-overlay.service';
import { ReplaySubject } from 'rxjs';
import { MergeTagPickerConfig } from '../merge-tag-picker.model';
import { BaseInputComponent } from '../../input/base-input/base-input.component';

export interface MergeTagPopoverOptions {
	placement?: string;
	offset?: [number, number];
}

@Component({
	selector: 'app-merge-tag-picker-input-base',
	templateUrl: './merge-tag-picker-input-base.component.html',
	styleUrl: './merge-tag-picker-input-base.component.scss'
})
export class MergeTagPickerInputBaseComponent extends BaseInputComponent implements OnDestroy {
	readonly mergeTagPickerConfig: MergeTagPickerConfig = {
		debounceMs: 200,
		minSearchLength: 2,
		searchPlaceholder: 'Search merge tags',
		noResultsLabel: 'No matches found',
		invalidDataLabel: 'Provide merge tag data to insert dynamic content',
		showExampleValues: true
	};

	private _enableMergeTags: boolean = false;

	@Input()
	set enableMergeTags(value: boolean) {
		this._enableMergeTags = value;
		if (!value) {
			this.closeMergeTagPicker();
		}
	}

	get enableMergeTags(): boolean {
		return this._enableMergeTags;
	}

	@Input()
	set mergeTagData(value: Record<string, any> | null) {
		this._mergeTagData = value;
		if (this.mergeTagSession) {
			this.mergeTagSession.updateData(value ?? null);
		}
	}

	get mergeTagData(): Record<string, any> | null {
		return this._mergeTagData;
	}

	private mergeTagPlacement: string = 'bottom-start';
	private mergeTagOffset: [number, number] = [0, 8];

	@Input()
	set mergeTagPopoverOptions(options: MergeTagPopoverOptions | null) {
		const normalized = this.normalizePopoverOptions(options);
		this.mergeTagPlacement = normalized.placement ?? 'bottom-start';
		this.mergeTagOffset = normalized.offset ?? [0, 8];
	}

	private _mergeTagData: Record<string, any> | null = null;

	public mergeTagPickerOpen: boolean = false;
	private mergeTagSession: MergeTagPickerSession | null = null;
	public pendingSlashIndex: number | null = null;
	public pendingCursorPosition: number = 0;
	private cursorTrackingCleanup?: () => void;
	private focusListenerCleanup?: () => void;
	public closeMergeTagPickerOnDestroy: boolean = true;

	public inputHistory$ = new ReplaySubject<string>(5);

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		if (!this.enableMergeTags) {
			return;
		}

		// The actual editable element
		const editorRoot: HTMLInputElement | undefined = this.getInputElement() as HTMLInputElement;
		// The wrapper element containing the editor
		const editorContainer: HTMLInputElement | undefined = this.getInputWrapperElement() as HTMLInputElement;

		if ((!editorRoot && !editorContainer) || (event.target !== editorRoot && event.target !== editorContainer)) {
			return;
		}

		// Only intercept '/' if the picker is NOT already open
		if (
			event.key === '/' &&
			this.enableMergeTags &&
			!this.mergeTagPickerOpen &&
			!event.metaKey &&
			!event.ctrlKey &&
			!event.altKey &&
			!event.defaultPrevented
		) {
			event.preventDefault();
			this.storeCursorPosition();
			this.pendingSlashIndex = this.pendingCursorPosition;

			setTimeout(() => {
				if (!this.enableMergeTags) {
					return;
				}

				this.openMergeTagPicker();
			}, 0);
			return;
		}

		setTimeout(() => this.storeCursorPosition(), 0);
	}

	constructor(public readonly mergeTagOverlayService: MergeTagPickerOverlayService) {
		super();
	}

	ngOnInit() {
		super.ngOnInit();

		const inputEl: HTMLInputElement = this.getInputElement() as HTMLInputElement;

		if (!!inputEl) {
			inputEl.addEventListener('focus', () => {
				this.inputHistory$.next(this.form.value.key);
				this.overrideMergeTagPickerIfNeeded();
			});

			inputEl.addEventListener('keydown', () => {
				this.inputHistory$.next(this.form.value.key);
			});

			const cursorTracker = () => this.storeCursorPosition();
			const cursorEvents: Array<keyof HTMLElementEventMap> = ['click', 'keyup', 'mouseup', 'select', 'input', 'focus'];
			cursorEvents.forEach(eventName => inputEl.addEventListener(eventName, cursorTracker));
			this.cursorTrackingCleanup = () => cursorEvents.forEach(eventName => inputEl.removeEventListener(eventName, cursorTracker));
		}

		if (this.enableMergeTags) {
			this.setupFocusListener();
		}

		this.storeCursorPosition();
	}

	// Override in subclass
	getInputElement(): HTMLElement {
		console.warn('getInputElement() not implemented in subclass!');
		return null;
	}

	// Override in subclass
	getInputWrapperElement(): HTMLElement {
		console.warn('getInputWrapperElement() not implemented in subclass!');
		return null;
	}

	// Override in subclass
	getInputValue(): string {
		console.warn('getInputValue() not implemented in subclass!');
		return '';
	}

	// Override in subclass
	getCursorIndex(): number {
		return (this.getInputElement() as HTMLInputElement)?.selectionStart;
	}

	public openMergeTagPicker(): void {
		// The actual editable element
		const inputEl: HTMLInputElement | undefined = this.getInputElement() as HTMLInputElement;

		if (!this.enableMergeTags || !inputEl) {
			return;
		}

		const anchor = () => this.getCaretClientRect(inputEl, this.getInputValue(), this.pendingCursorPosition);
		const avoidArea = () => this.getInputElement()?.getBoundingClientRect();

		if (this.mergeTagPickerOpen && this.mergeTagSession) {
			this.mergeTagSession.resetManualPosition();
			this.mergeTagSession.updateAnchor(anchor);
			this.mergeTagSession.updateData(this.mergeTagData ?? null);
			this.mergeTagOverlayService.focusSearchInput();
			return;
		}

		this.mergeTagSession = this.mergeTagOverlayService.open({
			anchor,
			avoidArea,
			data: this.mergeTagData ?? null,
			config: this.mergeTagPickerConfig,
			offset: this.mergeTagOffset,
			placement: this.mergeTagPlacement,
			onSelect: tag => this.onMergeTagSelected(tag),
			onClose: () => this.handleMergeTagPickerClosed(true),
			onOverride: () => this.closeMergeTagPicker(false)
		});

		this.mergeTagPickerOpen = true;
	}

	private handleMergeTagPickerClosed(triggerServiceClose: boolean = false): void {
		this.closeMergeTagPicker(triggerServiceClose);
		this.focusInputAt(this.pendingCursorPosition);
	}

	/**
	 * If there is an active merge tag session, reopen the picker with our current context
	 * @private
	 */
	private overrideMergeTagPickerIfNeeded(): void {
		if (this.enableMergeTags) {
			if (!this.mergeTagPickerOpen && this.mergeTagOverlayService.isSessionActive()) {
				this.openMergeTagPicker();
			}
		}
	}

	public onMergeTagSelected(tag: string, emit: boolean = true, patch: boolean = true) {
		const control = this.form.get(this.controlName);
		const currentValue: string = control?.value || '';
		let sanitizedValue = currentValue;
		let insertionIndex = this.resolveInsertionIndex();

		if (this.pendingSlashIndex != null && this.pendingSlashIndex > -1 && sanitizedValue.charAt(this.pendingSlashIndex) === '/') {
			sanitizedValue = sanitizedValue.slice(0, this.pendingSlashIndex) + sanitizedValue.slice(this.pendingSlashIndex + 1);
			if (insertionIndex > this.pendingSlashIndex) {
				insertionIndex = Math.max(this.pendingSlashIndex, insertionIndex - 1);
			}
		}

		const prefix = sanitizedValue.slice(0, insertionIndex);
		const suffix = sanitizedValue.slice(insertionIndex);
		const nextValue = `${prefix}${tag}${suffix}`;

		if (patch) {
			control?.patchValue(nextValue);
		}

		this.inputHistory$.next(nextValue);

		if (emit) {
			this.emitValueChange(nextValue);
		}

		this.pendingCursorPosition = insertionIndex + tag.length;
		this.pendingSlashIndex = null;

		setTimeout(() => {
			this.focusInputAt(this.pendingCursorPosition);
			this.updateOverlayAnchor();
		}, 0);
	}

	private getCaretClientRect(inputEl: HTMLInputElement, value: string, position: number): DOMRect {
		const inputRect = inputEl.getBoundingClientRect();
		if (typeof window === 'undefined') {
			return inputRect;
		}

		const style = window.getComputedStyle(inputEl);
		const context = this.getMeasurementContext(style);
		const textBeforeCaret = value?.slice(0, position) || '';
		const metrics = context.measureText(textBeforeCaret);
		const paddingLeft = parseFloat(style.paddingLeft || '0');
		const borderLeft = parseFloat(style.borderLeftWidth || '0');
		const letterSpacingRaw = style.letterSpacing || '0';
		const letterSpacing = letterSpacingRaw === 'normal' ? 0 : parseFloat(letterSpacingRaw);
		const scrollLeft = inputEl.scrollLeft;
		const rawX = inputRect.left + paddingLeft + borderLeft + metrics.width + position * letterSpacing - scrollLeft;
		const minX = inputRect.left + paddingLeft + borderLeft;
		const maxX = inputRect.right - 16;
		const clampedX = Math.min(Math.max(rawX, minX), maxX);
		const y = inputRect.bottom;

		return new DOMRect(clampedX, y, 0, inputRect.height);
	}

	private measurementContext?: CanvasRenderingContext2D;

	private getMeasurementContext(style: CSSStyleDeclaration): CanvasRenderingContext2D {
		if (!this.measurementContext) {
			const canvas = document.createElement('canvas');
			this.measurementContext = canvas.getContext('2d') || undefined;
		}

		const context = this.measurementContext;
		if (!context) {
			throw new Error('Unable to create text measurement context');
		}

		const fontStyle = [style.fontStyle, style.fontVariant, style.fontWeight, style.fontSize, style.fontFamily]
			.filter(Boolean)
			.join(' ');
		context.font = fontStyle;

		return context;
	}

	private closeMergeTagPicker(triggerServiceClose: boolean = true): void {
		if (!this.mergeTagPickerOpen) {
			return;
		}

		const session = this.mergeTagSession;
		this.mergeTagSession = null;
		this.mergeTagPickerOpen = false;
		this.pendingSlashIndex = null;

		if (triggerServiceClose && session) {
			session.close();
		}
	}

	private normalizePopoverOptions(options: MergeTagPopoverOptions | null | undefined): MergeTagPopoverOptions {
		if (!options) {
			return {};
		}

		const normalized: MergeTagPopoverOptions = {};

		if (typeof options.placement === 'string') {
			normalized.placement = options.placement;
		}

		if (Array.isArray(options.offset) && options.offset.length === 2) {
			const [skid, distance] = options.offset as [number, number];
			normalized.offset = [skid, distance];
		}

		return normalized;
	}

	public storeCursorPosition(): void {
		const inputEl: HTMLInputElement | undefined = this.getInputElement() as HTMLInputElement;
		if (!inputEl) {
			return;
		}

		const selectionStart = this.getCursorIndex();
		if (selectionStart == null) {
			const fallback = this.getInputValue()?.length ?? 0;
			this.pendingCursorPosition = fallback;
		} else {
			this.pendingCursorPosition = selectionStart;
		}

		if (this.mergeTagPickerOpen) {
			this.updateOverlayAnchor();
		}
	}

	public resolveInsertionIndex(): number {
		const inputEl: HTMLInputElement | undefined = this.getInputElement() as HTMLInputElement;
		if (!inputEl) {
			return this.pendingCursorPosition;
		}

		const selectionStart = this.getCursorIndex();
		if (selectionStart == null) {
			return this.pendingCursorPosition;
		}

		this.pendingCursorPosition = selectionStart;
		return selectionStart;
	}

	public updateOverlayAnchor(): void {
		if (!this.mergeTagPickerOpen || !this.mergeTagSession) {
			return;
		}

		const inputEl: HTMLInputElement | undefined = this.getInputElement() as HTMLInputElement;
		if (!inputEl) {
			return;
		}

		const anchor = () => this.getCaretClientRect(inputEl, this.getInputValue(), this.pendingCursorPosition);
		this.mergeTagSession.updateAnchor(anchor);
	}

	public focusInputAt(position: number) {
		const inputEl: HTMLInputElement | undefined = this.getInputElement() as HTMLInputElement;
		if (!inputEl) {
			return;
		}

		inputEl.focus();
		inputEl.setSelectionRange(position, position);
		this.pendingCursorPosition = position;
	}

	private setupFocusListener(): void {
		const wrapperEl: HTMLElement | undefined = this.getInputWrapperElement() as HTMLElement;
		const handler = (ev: FocusEvent) => {
			// Only react when the actual input gains focus
			const inputEl: HTMLInputElement | undefined = this.getInputElement() as HTMLInputElement;
			if (!inputEl || ev.target !== inputEl) return;

			this.inputHistory$.next(this.form.value.key);
			this.overrideMergeTagPickerIfNeeded();
		};

		// Attach to wrapper if available
		if (!wrapperEl) {
			return;
		}
		const attachTarget: EventTarget = wrapperEl;
		attachTarget.addEventListener('focus', handler, { capture: true });

		this.focusListenerCleanup = () => attachTarget.removeEventListener('focus', handler, { capture: true });
	}

	ngOnDestroy() {
		this.cursorTrackingCleanup?.();
		this.focusListenerCleanup?.();

		if (this.closeMergeTagPickerOnDestroy) {
			this.closeMergeTagPicker(true);
		}

		super.ngOnDestroy();
	}
}
